import { styled } from '@mui/material/styles'
import { ReactNode } from 'react'
import { useTheme, Container, Typography, ListItem } from '@mui/material'
import Layout from '../../../layouts'
import Page from '@/components/Page'
import { settingService, Section, GlobalSetting, SeoMetadata } from '@/services/page'
import pathService from '@/services/path'
import type { GetStaticPaths, GetStaticProps } from 'next'
import { APPS } from '@/appConfig'
import type { ParsedUrlQuery } from 'querystring'
import { notEmpty } from '@/utils/common'
import useApp from '@/hooks/useApp'

interface PathProps extends ParsedUrlQuery {
  appId: string
}

interface IndexProps {
  title?: string
  sections?: Section[]
  globalSetting?: GlobalSetting
  seoMetadataItemsCollection?: SeoMetadata[]
}

const RootStyle = styled('div')(({ theme }) => ({
  paddingTop: theme.spacing(8),
  [theme.breakpoints.up('md')]: {
    paddingTop: theme.spacing(11),
  },
}))

PrivacyPage.getLayout = function getLayout(page: React.ReactElement) {
  return <Layout variant="main">{page}</Layout>
}

// ----------------------------------------------------------------------

export default function PrivacyPage({ title }: IndexProps) {
  const { id: appId, logo, host, title: rootTitle, description: rootDescription } = useApp()

  const CustomListItem = ({ children }: { children: ReactNode }) => (
    <ListItem sx={{ display: 'list-item' }}>{children}</ListItem>
  )

  return (
    <Page title="Privacy">
      <RootStyle>
        <Container>
          <Typography variant="h1">Privacy</Typography>
          <Typography variant="caption">Friday, December 23, 2022</Typography>
          <Typography variant="body1" mt={5}>
            We at {rootTitle} (the “Company”, “we”, or “us”) are committed to safeguarding the
            privacy of our customers. This Privacy Policy entails the way in which your Personal
            Information or Usage Information will be collected, used, shared, stored, protected or
            disclosed by our service. This Privacy Policy applies to the mobile application or
            website developed by us in relation to services provided to you by the Company from time
            to time (“Services”). By using our Services, you agree to be bound by the terms of this
            Privacy Policy. If you do not accept the terms of the Privacy Policy, you are directed
            to discontinue accessing/using our application in any way. We strongly recommend that
            you read the policy carefully before proceeding with any transaction on our application.
          </Typography>
          <Typography variant="h5" mt={3}>
            Why we collect your information?
          </Typography>
          <Typography variant="body1" mt={1}>
            We collect information about you for the following general purposes: For registration
            and to manage your account, including to facilitate your access to and use of our
            Services; to communicate with you in general, including to provide information about us;
            to enable us to publish your reviews, forum posts, and other content; to respond to your
            questions and comments, to prevent potentially prohibited or illegal activities and to
            enforce our Terms of Use.
          </Typography>
          <Typography variant="h5" mt={3}>
            Collecting and Using Your Personal Data
          </Typography>
          <Typography variant="body1" mt={1}>
            When you visit or use our Services, we collect information about your computer or mobile
            device. Some of the information we collect include:
            <CustomListItem>
              Computer IP addresses mobile device ids (the unique identifier assigned to a device by
              a manufacturer), technical information about your computer or mobile device (such as
              type of device, web browser or operating system).
            </CustomListItem>
            <CustomListItem>
              Your mobile device’s geographic location (specific geographic location if you’ve
              enabled collection of that information, or general geographic location automatically).
            </CustomListItem>
            <CustomListItem>
              Cookies: If you are accessing our mobile application through computers we place
              “Cookies” on your computer to identify it. “Cookies” are identifiers we transfer to
              your computer or mobile device that allow us to recognize your browser or mobile
              device and tell us how and when pages and features in our Services are visited and by
              how many people. You may be able to change the preferences on your browser or mobile
              device to prevent or limit your computer or device’s acceptance of cookies, but this
              may prevent you from taking advantage of some of our features. If you click on a link
              to a third party website, such third party may also transmit cookies to you. You
              hereby agree that this Privacy Policy does not cover the use of cookies by any third
              parties.
            </CustomListItem>
          </Typography>
          <Typography variant="h5" mt={3}>
            Personal Data
          </Typography>
          <Typography variant="body1" mt={1}>
            While using Our Service, We may ask You to provide Us with certain personally
            identifiable information that can be used to contact or identify You. Personally
            identifiable information may include, but is not limited to:
            <CustomListItem>Email address</CustomListItem>
            <CustomListItem>First name and last name</CustomListItem>
            <CustomListItem>Address, State, Province, ZIP/Postal code, City</CustomListItem>
            <CustomListItem>Usage and content generation Data</CustomListItem>
          </Typography>
        </Container>
      </RootStyle>
    </Page>
  )
}

export const getStaticPaths: GetStaticPaths<PathProps> = async () => {
  const { getDefaultPaths } = pathService()
  const paths = APPS.flatMap((app) => getDefaultPaths(app.id))

  return {
    paths,
    fallback: true,
  }
}

export const getStaticProps: GetStaticProps<IndexProps, PathProps> = async ({ params, locale }) => {
  if (!params) throw new Error('No path parameters found')

  const { appId } = params

  const { getGlobalSetting } = settingService(appId)
  const globalSetting = await getGlobalSetting({ locale })

  return {
    props: {
      title: globalSetting.seoMetadataItemsMap.title,
      globalSetting,
    },
    revalidate: 3600,
  }
}
